import {
  EmailOutlined,
  LocalPhoneOutlined,
  OpenInNew,
  PlaceOutlined,
  VerifiedOutlined,
  WorkspacePremiumOutlined,
} from '@mui/icons-material';
import { Box, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SourcingProvenance, type SourcingOpportunity, type SupplierAddress } from '@/api/generatedApi';
import { SOURCING_CONFIDENCE } from '@/components/PartFinder/confidence/confidence';
import { PercentConfidencePill } from '@/components/PartFinder/confidence/ConfidencePill';
import { StyledHeaderCell, StyledTable, StyledTableCell, StyledTableRow } from './NsnTable';
import { PartFinderFeedbackControl } from './PartFinderFeedbackControl';

const EMPTY_VALUE = '—';

interface SourcingOpportunitiesTableProperties {
  reportId: string;
  opportunities: SourcingOpportunity[];
}

const SupplierHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '30%',
  textAlign: 'left',
  paddingLeft: '8px',
}));

const PartNumberHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '150px',
}));

const DetailHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '64px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
}));

const PriceHeaderCell = styled(StyledHeaderCell)(() => ({
  minWidth: '120px',
  width: '120px',
  whiteSpace: 'nowrap',
}));

const ContactHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '240px',
  textAlign: 'left',
}));

const ConfidenceHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '96px',
  textAlign: 'center',
}));

const FeedbackHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '76px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
}));

const FeedbackCell = styled(StyledTableCell)(() => ({
  width: '76px',
  verticalAlign: 'top',
}));

const SupplierCell = styled(StyledTableCell)(() => ({
  fontWeight: 500,
  width: '30%',
  textAlign: 'left',
  paddingLeft: '8px',
  verticalAlign: 'top',
}));

const PartNumberCell = styled(StyledTableCell)(() => ({
  width: '150px',
  verticalAlign: 'top',
  overflowWrap: 'break-word',
}));

const DetailCell = styled(StyledTableCell)(() => ({
  width: '64px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  verticalAlign: 'top',
}));

const PriceCell = styled(StyledTableCell)(() => ({
  minWidth: '120px',
  width: '120px',
  verticalAlign: 'top',
}));

const ConfidenceCell = styled(StyledTableCell)(() => ({
  width: '96px',
  textAlign: 'center',
  verticalAlign: 'top',
}));

const ContactCell = styled(StyledTableCell)(() => ({
  width: '240px',
  verticalAlign: 'top',
}));

const SupplierNameContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

const SupplierNameRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const ReasoningSubtext = styled(Box)(({ theme }) => ({
  fontSize: '11px',
  lineHeight: '1.4',
  color: theme.palette.text.secondary,
  marginTop: '4px',
}));

const AccreditationBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '11px',
  fontWeight: 500,
  color: theme.palette.success.main,
}));

const AccreditationIcon = styled(WorkspacePremiumOutlined)(() => ({
  fontSize: '14px',
}));

const VerifiedIcon = styled(VerifiedOutlined)(({ theme }) => ({
  fontSize: '20px',
  color: theme.palette.success.main,
}));

const LinkIcon = styled(OpenInNew)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.info.main,
  marginLeft: '6px',
  verticalAlign: 'middle',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.info.dark,
  },
}));

const SupplierLink = styled('a')(({ theme }) => ({
  color: theme.palette.info.main,
  fontWeight: 500,
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const PlainSupplierName = styled(Box)(() => ({
  fontWeight: 500,
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: '14px',
  backgroundColor: theme.palette.background.default,
  borderRadius: '4px',
  border: `1px solid ${theme.palette.divider}`,
}));

const ContactLines = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

const ContactLine = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '6px',
  fontSize: '11px',
  lineHeight: 1.4,
}));

const ContactGlyph = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  marginTop: '1px',
  color: theme.palette.text.secondary,
  '& svg': {
    fontSize: '14px',
  },
}));

const ContactLink = styled('a')(({ theme }) => ({
  color: theme.palette.info.main,
  textDecoration: 'none',
  wordBreak: 'break-word',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const VERIFIED_TOOLTIP = 'Confirmed live inventory from a verified supplier';

const VerifiedSupplierTag = () => (
  <Tooltip title={VERIFIED_TOOLTIP}>
    <VerifiedIcon aria-label={VERIFIED_TOOLTIP} />
  </Tooltip>
);

const ConfidenceContent = ({ opportunity }: { opportunity: SourcingOpportunity }) => {
  if (opportunity.provenance === SourcingProvenance.VerifiedSupplier) {
    return <VerifiedSupplierTag />;
  }
  if (opportunity.confidenceScore === undefined) {
    return <>{EMPTY_VALUE}</>;
  }
  return <PercentConfidencePill percent={Math.round(opportunity.confidenceScore)} explanation={SOURCING_CONFIDENCE} />;
};

const IlsMatchedOn = ({ opportunity }: { opportunity: SourcingOpportunity }) => {
  const searchTerm = opportunity.matchedOn?.[0];
  const isVerifiedSupplier = opportunity.provenance === SourcingProvenance.VerifiedSupplier;
  return isVerifiedSupplier && searchTerm && <ReasoningSubtext>Matched on {searchTerm}</ReasoningSubtext>;
};

export const formatPrice = (price: number | string): string => `$${Number(price).toFixed(2)}`;

const SupplierLinkWithIcon = ({
  url,
  children,
  tooltipText = 'Open in new tab',
}: {
  url: string;
  children: React.ReactNode;
  tooltipText?: string;
}) => (
  <SupplierNameRow>
    {children && (
      <SupplierLink href={url} target='_blank' rel='noopener noreferrer'>
        {children}
      </SupplierLink>
    )}
    <Tooltip title={tooltipText}>
      <Box component='a' href={url} target='_blank' rel='noopener noreferrer' sx={{ lineHeight: 0 }}>
        <LinkIcon />
      </Box>
    </Tooltip>
  </SupplierNameRow>
);

const SupplierName = ({ supplier, url }: { supplier: string; url?: string }) => {
  if (url) {
    return <SupplierLinkWithIcon url={url}>{supplier}</SupplierLinkWithIcon>;
  }

  return <PlainSupplierName>{supplier}</PlainSupplierName>;
};

const PriceAvailabilityCell = ({ price, availability }: { price?: number | null; availability?: string }) => {
  if (!price && !availability) {
    return <PriceCell>{EMPTY_VALUE}</PriceCell>;
  }

  return (
    <PriceCell>
      {price && <Box sx={{ fontSize: '12px', fontWeight: 500 }}>{formatPrice(price)}</Box>}
      {availability && (
        <Box sx={theme => ({ fontSize: '11px', color: theme.palette.text.secondary, marginTop: '2px' })}>
          {availability}
        </Box>
      )}
    </PriceCell>
  );
};

const formatAddress = (address: SupplierAddress): string => {
  const cityRegion = [address.city, address.stateProvince].filter(Boolean).join(', ');
  const cityRegionPostal = [cityRegion, address.postalCode].filter(Boolean).join(' ');
  return [address.street, cityRegionPostal, address.country].filter(Boolean).join(', ');
};

const accreditationLabel = (level?: string, preferred?: boolean): string =>
  [level, preferred ? 'Preferred' : undefined].filter(Boolean).join(' · ');

const ContactColumn = ({ opportunity }: { opportunity: SourcingOpportunity }) => {
  const { supplierAddress, rfqContact } = opportunity;
  const address = supplierAddress ? formatAddress(supplierAddress) : '';
  const email = rfqContact?.email;
  const phone = rfqContact?.phone;

  if (!address && !email && !phone) {
    return <>{EMPTY_VALUE}</>;
  }

  return (
    <ContactLines>
      {address && (
        <ContactLine>
          <ContactGlyph>
            <PlaceOutlined />
          </ContactGlyph>
          <span>{address}</span>
        </ContactLine>
      )}
      {email && (
        <ContactLine>
          <ContactGlyph>
            <EmailOutlined />
          </ContactGlyph>
          <ContactLink href={`mailto:${email}`}>{email}</ContactLink>
        </ContactLine>
      )}
      {phone && (
        <ContactLine>
          <ContactGlyph>
            <LocalPhoneOutlined />
          </ContactGlyph>
          <ContactLink href={`tel:${phone}`}>{phone}</ContactLink>
        </ContactLine>
      )}
    </ContactLines>
  );
};

export const SourcingOpportunitiesTable = ({ reportId, opportunities }: SourcingOpportunitiesTableProperties) => {
  if (opportunities.length === 0) {
    return (
      <EmptyStateContainer>No sourcing opportunities were found during this report generation.</EmptyStateContainer>
    );
  }

  return (
    <StyledTable size='small'>
      <TableHead>
        <TableRow>
          <SupplierHeaderCell>Supplier</SupplierHeaderCell>
          <PartNumberHeaderCell>Part Number</PartNumberHeaderCell>
          <DetailHeaderCell>CAGE</DetailHeaderCell>
          <DetailHeaderCell>Condition</DetailHeaderCell>
          <DetailHeaderCell>Qty</DetailHeaderCell>
          <PriceHeaderCell>Price / Availability</PriceHeaderCell>
          <ContactHeaderCell>Contact</ContactHeaderCell>
          <ConfidenceHeaderCell>Confidence</ConfidenceHeaderCell>
          <FeedbackHeaderCell>Feedback</FeedbackHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {opportunities.map(opportunity => {
          const accreditation = accreditationLabel(opportunity.accreditedVendorLevel, opportunity.isPreferredVendor);
          return (
            <StyledTableRow key={opportunity.id} id={`sourcing-opportunity-${opportunity.id}`}>
              <SupplierCell>
                <SupplierNameContainer>
                  <SupplierName supplier={opportunity.supplier} url={opportunity.url} />
                  {accreditation && (
                    <AccreditationBadge>
                      <AccreditationIcon />
                      <span>{accreditation}</span>
                    </AccreditationBadge>
                  )}
                  {opportunity.reasoning && <ReasoningSubtext>{opportunity.reasoning}</ReasoningSubtext>}
                </SupplierNameContainer>
              </SupplierCell>
              <PartNumberCell>
                {opportunity.partNumber ?? EMPTY_VALUE}
                <IlsMatchedOn opportunity={opportunity} />
              </PartNumberCell>
              <DetailCell>{opportunity.cageCode ?? EMPTY_VALUE}</DetailCell>
              <DetailCell>{opportunity.conditionCode ?? EMPTY_VALUE}</DetailCell>
              <DetailCell>{opportunity.quantityAvailable ?? EMPTY_VALUE}</DetailCell>
              <PriceAvailabilityCell price={opportunity.price} availability={opportunity.availability} />
              <ContactCell>
                <ContactColumn opportunity={opportunity} />
              </ContactCell>
              <ConfidenceCell>
                <ConfidenceContent opportunity={opportunity} />
              </ConfidenceCell>
              <FeedbackCell>
                {opportunity.provenance === SourcingProvenance.WebDiscovered && (
                  <PartFinderFeedbackControl reportId={reportId} targetId={opportunity.id} align='center' />
                )}
              </FeedbackCell>
            </StyledTableRow>
          );
        })}
      </TableBody>
    </StyledTable>
  );
};
